// src/App.tsx
import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Layout, ConfigProvider, Menu, Button, Dropdown, Drawer } from 'antd';
import type { ThemeConfig } from 'antd/es/config-provider/context';
import { UserOutlined, LogoutOutlined, DashboardOutlined, FileTextOutlined, BarChartOutlined, TeamOutlined, SettingOutlined, MessageOutlined, CalendarOutlined, MenuOutlined, TrophyOutlined } from '@ant-design/icons';

import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { WelcomePage } from './pages/WelcomePage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { FaqPage } from './pages/FaqPage';
import { Dashboard } from './pages/Dashboard';
import { DoctorDashboard } from './pages/DoctorDashboard';
import { GlobalSearch } from './components/GlobalSearch';
import { Breadcrumbs } from './components/Breadcrumbs';
import { ProfilePage } from './pages/ProfilePage';
import { GlucoseDiary } from './pages/GlucoseDiary';
import { DiaryHubPage } from './pages/DiaryHubPage';
import { NutritionDiary } from './pages/NutritionDiary';
import { ChartsPage } from './pages/ChartsPage';
import { DoctorsPage } from './pages/DoctorsPage';
import { PatientDetailPage } from './pages/PatientDetailPage';
import { AdminPage } from './pages/AdminPage';
import { AdminPatientDiaryPage } from './pages/AdminPatientDiaryPage';
import { AdminChatsPage } from './pages/AdminChatsPage';
import { ChatPage } from './pages/ChatPage';
import { DoctorsChatsPage } from './pages/DoctorsChatsPage';
import { CalendarPage } from './pages/CalendarPage';
import { WeightDiary } from './pages/WeightDiary';
import { MovementsDiary } from './pages/MovementsDiary';
import { SymptomsDiary } from './pages/SymptomsDiary';
import { AbdomenDiary } from './pages/AbdomenDiary';
import { VisitsDiary } from './pages/VisitsDiary';
import { LabTestsDiary } from './pages/LabTestsDiary';
import { GamificationPage } from './pages/GamificationPage';
import { appDesign } from './theme/appDesign';

const { Header, Content, Footer } = Layout;

// Theme — single source: theme/appDesign.ts
const themeConfig: ThemeConfig = {
  token: {
    colorPrimary: appDesign.primary,
    colorInfo: '#42A5F5',
    colorSuccess: '#66BB6A',
    fontFamily: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSize: 15,
    borderRadius: appDesign.radiusMd,
  },
  components: {
    Layout: {
      headerBg: 'transparent',
      bodyBg: appDesign.pageBg,
    },
    Card: {
      colorBgContainer: appDesign.white,
      borderRadiusLG: appDesign.radiusLg,
    },
    Button: {
      colorPrimary: appDesign.primary,
      colorPrimaryHover: appDesign.primaryHover,
      colorPrimaryActive: appDesign.primaryActive,
      borderRadius: appDesign.radiusSm,
      controlHeight: 44,
    },
    Input: {
      borderRadius: appDesign.radiusSm,
      colorBorder: appDesign.gradientMid,
      activeBorderColor: appDesign.primary,
      hoverBorderColor: '#8BC34A',
    },
    Table: {
      colorBgContainer: appDesign.white,
      borderRadiusLG: appDesign.radiusMd,
    },
    Menu: {
      itemSelectedBg: 'rgba(124, 179, 66, 0.12)',
      itemHoverBg: 'rgba(124, 179, 66, 0.08)',
      horizontalItemSelectedColor: appDesign.primaryActive,
    },
  },
};

// Auth Context
interface AuthContextType {
  user: any;
  login: (userData: any) => void;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<any>(() => {
    try {
      const cached = localStorage.getItem('user_data');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const profileAbortRef = React.useRef<AbortController | null>(null);

  const clearSession = React.useCallback(() => {
    profileAbortRef.current?.abort();
    profileAbortRef.current = null;
    localStorage.removeItem('access_token');
    localStorage.removeItem('user_data');
    setUser(null);
  }, []);

  useEffect(() => {
    const onSessionExpired = () => clearSession();
    window.addEventListener('auth-session-expired', onSessionExpired);
    return () => window.removeEventListener('auth-session-expired', onSessionExpired);
  }, [clearSession]);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const userData = localStorage.getItem('user_data');
    if (!token || !userData) {
      return;
    }

    try {
      setUser(JSON.parse(userData));
    } catch {
      clearSession();
      return;
    }

    profileAbortRef.current?.abort();
    const controller = new AbortController();
    profileAbortRef.current = controller;

    const loadUser = async () => {
      try {
        const { authAPI } = await import('./utils/api');
        const response = await authAPI.getProfile({ signal: controller.signal });
        const userDataFromAPI = response.data as any;
        if (userDataFromAPI && !controller.signal.aborted) {
          setUser(userDataFromAPI);
          localStorage.setItem('user_data', JSON.stringify(userDataFromAPI));
        }
      } catch (error: any) {
        if (controller.signal.aborted) {
          return;
        }
        if (error?.response?.status === 401) {
          clearSession();
          return;
        }
        console.error('Failed to load user from API:', error);
      }
    };
    loadUser();

    return () => {
      controller.abort();
    };
  }, [clearSession]);

  const login = (userData: any) => {
    profileAbortRef.current?.abort();
    profileAbortRef.current = null;
    if (!userData?.access_token || !userData?.user) {
      console.error('Invalid login payload', userData);
      return;
    }
    localStorage.setItem('access_token', userData.access_token);
    localStorage.setItem('user_data', JSON.stringify(userData.user));
    setUser(userData.user);
  };

  const logout = () => {
    clearSession();
  };

  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider value={{ user, login, logout, isAuthenticated }}>
      {children}
    </AuthContext.Provider>
  );
};

// App Layout Component (inside AuthProvider)
const AppLayout: React.FC = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);

  // Determine current route for menu highlighting
  const getCurrentKey = () => {
    const path = location.pathname;
    if (path === '/' || path === '/dashboard') return 'dashboard';
    if (path === '/profile') return 'profile';
    if (path === '/diary/glucose') return 'glucose';
    if (path === '/diary/nutrition') return 'nutrition';
    if (path === '/calendar') return 'calendar';
    if (path === '/charts') return 'charts';
    if (path === '/gamification') return 'gamification';
    if (path === '/doctors') return 'doctors';
    if (path === '/admin') return 'admin';
    if (path === '/chat' || path.startsWith('/chat/')) return 'chat';
    if (path === '/chats') return 'chats';
    if (path.startsWith('/diary/')) {
      const diaryType = path.split('/diary/')[1];
      return diaryType || 'glucose';
    }
    return '';
  };

  // Menu items based on user role
  const getMenuItems = () => {
    if (!isAuthenticated) return [];
    
    // For patients - show all diaries
    if (user?.role === 'patient') {
      return [
        {
          key: 'dashboard',
          icon: <DashboardOutlined />,
          label: 'Главная',
          onClick: () => navigate('/dashboard')
        },
        {
          key: 'calendar',
          icon: <CalendarOutlined />,
          label: 'Календарь',
          onClick: () => navigate('/calendar')
        },
        {
          type: 'divider' as const
        },
        {
          key: 'diaries',
          icon: <FileTextOutlined />,
          label: 'Дневники',
          children: [
            {
              key: 'glucose',
              label: 'Дневник глюкозы',
              onClick: () => navigate('/diary/glucose')
            },
            {
              key: 'nutrition',
              label: 'Дневник питания',
              onClick: () => navigate('/diary/nutrition')
            },
            {
              key: 'weight',
              label: 'Дневник веса',
              onClick: () => navigate('/diary/weight')
            },
            {
              key: 'movements',
              label: 'Дневник шевелений',
              onClick: () => navigate('/diary/movements')
            },
            {
              key: 'symptoms',
              label: 'Дневник симптомов',
              onClick: () => navigate('/diary/symptoms')
            },
            {
              key: 'abdomen',
              label: window.innerWidth < 768 ? 'Окружность живота' : 'Дневник окружности живота',
              onClick: () => navigate('/diary/abdomen')
            },
            {
              key: 'visits',
              label: 'Дневник посещений',
              onClick: () => navigate('/diary/visits')
            },
            {
              key: 'lab-tests',
              label: 'Дневник анализов',
              onClick: () => navigate('/diary/lab-tests')
            }
          ]
        },
        {
          key: 'charts',
          icon: <BarChartOutlined />,
          label: 'Графики',
          onClick: () => navigate('/charts')
        },
        {
          key: 'chat',
          icon: <MessageOutlined />,
          label: 'Чат',
          onClick: () => navigate('/chat')
        },
        {
          key: 'gamification',
          icon: <TrophyOutlined />,
          label: 'Геймификация',
          onClick: () => navigate('/gamification')
        }
      ];
    }
    
    // For doctors - show doctor workspace
    if (user?.role === 'doctor') {
      return [
        {
          key: 'dashboard',
          icon: <DashboardOutlined />,
          label: 'Рабочее место',
          onClick: () => navigate('/dashboard')
        },
        {
          key: 'charts',
          icon: <BarChartOutlined />,
          label: 'Графики',
          onClick: () => navigate('/charts')
        },
        {
          key: 'doctors',
          icon: <TeamOutlined />,
          label: 'Пациенты',
          onClick: () => navigate('/doctors')
        },
        {
          key: 'chats',
          icon: <MessageOutlined />,
          label: 'Чаты',
          onClick: () => navigate('/chats')
        }
      ];
    }
    
    // For admins - admin panel and support chats
    if (user?.role === 'admin') {
      return [
        {
          key: 'admin',
          icon: <SettingOutlined />,
          label: 'Админка',
          onClick: () => navigate('/admin')
        },
        {
          key: 'chats',
          icon: <MessageOutlined />,
          label: 'Чаты',
          onClick: () => navigate('/admin/chats')
        }
      ];
    }
    
    // Fallback
    return [];
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Handle window resize for mobile detection
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const userMenuItems = [
    {
      key: 'profile',
      icon: <SettingOutlined />,
      label: 'Профиль',
      onClick: () => navigate('/profile')
    },
    {
      type: 'divider' as const
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Выйти',
      onClick: handleLogout,
      danger: true
    }
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {isAuthenticated && (
        <Header style={{
          background: 'linear-gradient(180deg, #E8F5E9 0%, #DCEDC8 100%)',
          padding: isMobile ? '0 12px' : '0 20px',
          boxShadow: '0 4px 20px rgba(76, 175, 80, 0.08)',
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          borderBottom: '1px solid rgba(124, 179, 66, 0.2)',
          height: isMobile ? '56px' : '64px',
          lineHeight: isMobile ? '56px' : '64px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: '100%'
          }}>
            {/* Mobile Menu Button */}
            {isMobile && (
              <Button
                type="text"
                icon={<MenuOutlined />}
                onClick={() => setMobileMenuOpen(true)}
                style={{
                  fontSize: '20px',
                  color: '#263238',
                  padding: '0 8px',
                  minWidth: 'auto'
                }}
              />
            )}

            {/* Logo - accessible navigation button */}
            <Button
              type="text"
              onClick={() => {
                if (user?.role === 'admin') {
                  navigate('/admin');
                } else {
                  navigate('/dashboard');
                }
              }}
              style={{
                fontSize: isMobile ? '18px' : '20px',
                fontWeight: '600',
                color: '#263238',
                userSelect: 'none',
                flex: isMobile ? 1 : 'none',
                textAlign: isMobile ? 'center' : 'left',
                marginLeft: isMobile ? 0 : 'auto',
                height: 'auto',
                padding: 0
              }}
              aria-label="Перейти на главную"
            >
              <span style={{ color: '#263238', fontWeight: 700 }}>compass</span>
              <span style={{ color: '#7CB342', fontWeight: 800, marginLeft: 4 }}>mum</span>
            </Button>

            {/* Desktop Navigation Menu */}
            {!isMobile && (
              <>
                <Menu
                  mode="horizontal"
                  selectedKeys={[getCurrentKey()]}
                  items={getMenuItems()}
                  onClick={({ key }) => {
                    const item = getMenuItems().find(i => i.key === key);
                    if (item?.onClick) item.onClick();
                  }}
                  style={{
                    flex: 1,
                    justifyContent: 'center',
                    backgroundColor: 'transparent',
                    borderBottom: 'none',
                    minWidth: 0
                  }}
                />
                <div style={{ margin: '0 16px', width: 300 }}>
                  <GlobalSearch />
                </div>
              </>
            )}

            {/* User Menu */}
            <Dropdown
              menu={{ items: userMenuItems }}
              placement="bottomRight"
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: isMobile ? '4px' : '8px',
                cursor: 'pointer',
                padding: '4px 12px',
                borderRadius: '8px',
                transition: 'background-color 0.3s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <UserOutlined style={{ fontSize: isMobile ? '16px' : '18px', color: '#7CB342' }} />
                {!isMobile && (
                  <span style={{
                    color: '#263238',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    {user?.full_name || 'Пользователь'}
                  </span>
                )}
              </div>
            </Dropdown>
          </div>
        </Header>
      )}

      {/* Mobile Drawer Menu */}
      {isMobile && isAuthenticated && (
        <Drawer
          title="Меню"
          placement="left"
          onClose={() => setMobileMenuOpen(false)}
          open={mobileMenuOpen}
          bodyStyle={{ padding: 0 }}
          width={280}
        >
          <Menu
            mode="inline"
            selectedKeys={[getCurrentKey()]}
            items={getMenuItems()}
            onClick={({ key }) => {
              const item = getMenuItems().find(i => i.key === key);
              if (item?.onClick) {
                item.onClick();
                setMobileMenuOpen(false);
              }
            }}
            style={{ borderRight: 'none' }}
          />
        </Drawer>
      )}

      <Content style={{ 
        margin: isAuthenticated ? (isMobile ? '12px 8px 0' : '20px 16px 0') : '0',
        backgroundColor: isAuthenticated ? '#F0F9F4' : 'transparent',
        padding: isAuthenticated ? '0' : '0',
        minHeight: isAuthenticated ? 'auto' : '100vh',
        width: '100%',
        maxWidth: '100%',
      }}>
        {isAuthenticated ? (
          <div className="app-content-shell" style={{ padding: isMobile ? 16 : 28, minHeight: 360 }}>
            <Breadcrumbs />
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route
                path="/dashboard"
                element={user?.role === 'doctor' ? <DoctorDashboard /> : <Dashboard />}
              />
              <Route
                path="/calendar"
                element={user?.role === 'doctor' ? <Navigate to="/dashboard" replace /> : <CalendarPage />}
              />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/diary" element={<DiaryHubPage />} />
              <Route path="/diary/glucose" element={<GlucoseDiary />} />
              <Route path="/diary/nutrition" element={<NutritionDiary />} />
              <Route path="/diary/weight" element={<WeightDiary />} />
              <Route path="/diary/movements" element={<MovementsDiary />} />
              <Route path="/diary/symptoms" element={<SymptomsDiary />} />
              <Route path="/diary/abdomen" element={<AbdomenDiary />} />
              <Route path="/diary/visits" element={<VisitsDiary />} />
              <Route path="/diary/lab-tests" element={<LabTestsDiary />} />
              <Route path="/charts" element={<ChartsPage />} />
              <Route path="/gamification" element={<GamificationPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/faq" element={<FaqPage />} />
            <Route
              path="/doctors"
              element={
                user?.role === 'doctor' || user?.role === 'admin'
                  ? <DoctorsPage />
                  : <Navigate to="/dashboard" replace />
              }
            />
            <Route
              path="/doctors/patient/:patientId"
              element={
                user?.role === 'doctor' || user?.role === 'admin'
                  ? <PatientDetailPage />
                  : <Navigate to="/dashboard" replace />
              }
            />
            <Route
              path="/chat"
              element={
                user?.role === 'patient' || user?.role === 'doctor'
                  ? <ChatPage />
                  : <Navigate to="/dashboard" replace />
              }
            />
            <Route
              path="/chat/:conversationId"
              element={
                user?.role === 'patient' || user?.role === 'doctor'
                  ? <ChatPage />
                  : <Navigate to="/dashboard" replace />
              }
            />
            <Route
              path="/chats"
              element={
                user?.role === 'doctor'
                  ? <DoctorsChatsPage />
                  : <Navigate to="/dashboard" replace />
              }
            />
            <Route 
              path="/admin" 
              element={
                user?.role === 'admin' ? <AdminPage /> : <Navigate to="/dashboard" replace />
              } 
            />
            <Route 
              path="/admin/patient/:patientId/diary" 
              element={
                user?.role === 'admin' ? <AdminPatientDiaryPage /> : <Navigate to="/dashboard" replace />
              } 
            />
            <Route 
              path="/admin/chats" 
              element={
                user?.role === 'admin' ? <AdminChatsPage /> : <Navigate to="/dashboard" replace />
              } 
            />
            <Route 
              path="/admin/chats/:conversationId" 
              element={
                user?.role === 'admin' ? <ChatPage /> : <Navigate to="/dashboard" replace />
              } 
            />
          </Routes>
          </div>
        ) : (
          <Routes>
            <Route path="/" element={<WelcomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        )}
      </Content>

      <Footer style={{
        textAlign: 'center',
        background: 'linear-gradient(0deg, #E8F5E9 0%, #F0F9F4 100%)',
        borderTop: '1px solid rgba(124, 179, 66, 0.15)',
        color: '#616161',
        fontSize: '14px',
        padding: '20px 16px',
      }}>
        <div style={{ marginBottom: 8, fontWeight: 600, color: '#263238' }}>Compass Mom</div>
        <div style={{ marginBottom: 8 }}>
          © {new Date().getFullYear()} — Забота о вас и вашем малыше
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px 16px', marginBottom: 8 }}>
          <Button type="link" onClick={() => navigate('/privacy-policy')} style={{ padding: 0, height: 'auto' }}>
            Политика конфиденциальности
          </Button>
          <Button type="link" onClick={() => navigate('/faq')} style={{ padding: 0, height: 'auto' }}>
            FAQ
          </Button>
          <a href="https://t.me/compass_mom" target="_blank" rel="noopener noreferrer" style={{ color: '#0088cc' }}>
            Telegram: @compass_mom
          </a>
          <a href="https://apps.apple.com/app/id6764552008" target="_blank" rel="noopener noreferrer" style={{ color: '#263238' }}>
            App Store
          </a>
        </div>
        <div style={{ color: '#757575', fontSize: 13 }}>
          Контакты: поддержка —{' '}
          <a href="https://t.me/compass_mom" target="_blank" rel="noopener noreferrer" style={{ color: '#558B2F' }}>
            @compass_mom
          </a>
          {' '}(Telegram)
        </div>
      </Footer>
    </Layout>
  );
};

// Main App Component
function App() {
  return (
    <ConfigProvider theme={themeConfig}>
      <AuthProvider>
        <Router>
          <AppLayout />
        </Router>
      </AuthProvider>
    </ConfigProvider>
  );
}

export default App;
